import type { LucideIcon } from "lucide-react";
import {
  BookUserIcon,
  ForwardIcon,
  GiftIcon,
  HeartIcon,
  HomeIcon,
  ImageIcon,
  MailIcon,
  MailsIcon,
  MessageSquareIcon,
  PaletteIcon,
  PuzzleIcon,
  RefreshCcwIcon,
  UserRoundIcon,
  UsersIcon,
  UsersRoundIcon,
} from "lucide-react";

export type NavGroupId =
  | "overview"
  | "fundraising"
  | "engagement"
  | "content"
  | "assistant"
  | "administration";

export const NAV_GROUP_ORDER: NavGroupId[] = [
  "overview",
  "fundraising",
  "engagement",
  "content",
  "assistant",
  "administration",
];

export const NAV_GROUP_LABELS: Record<NavGroupId, string> = {
  overview: "Overview",
  fundraising: "Fundraising",
  engagement: "Engagement",
  content: "Content",
  assistant: "Assistant",
  administration: "Administration",
};

export interface Route {
  name: string;
  icon: LucideIcon;
  path: string;
  hidden: boolean;
  children?: Route[];
  adminOnly?: boolean;
  /** Sidebar section (Atlas-style groups). */
  group?: NavGroupId;
  /** Shown in nav but not navigable yet. */
  disabled?: boolean;
  /** Tooltip when `disabled` is true. */
  disabledTitle?: string;
}

export type SettingsNavGroupId = "account" | "setup" | "administration";

export const SETTINGS_NAV_GROUP_ORDER: SettingsNavGroupId[] = [
  "account",
  "setup",
  "administration",
];

export const SETTINGS_NAV_GROUP_LABELS: Record<SettingsNavGroupId, string> = {
  account: "Account",
  setup: "Setup",
  administration: "Administration",
};

export type SettingsNavItem = {
  name: string;
  path: string;
  icon: LucideIcon;
  adminOnly?: boolean;
};

/** Settings sidenav + route metadata for `findSettingsRouteByPathname`. */
export const settingsNavItems: SettingsNavItem[] = [
  {
    name: "Branding",
    path: "/settings/brand",
    icon: PaletteIcon,
  },
  {
    name: "Identity",
    path: "/settings/identity",
    icon: BookUserIcon,
  },
  {
    name: "Installation",
    path: "/settings/installation",
    icon: ForwardIcon,
  },
  {
    name: "Integrations",
    path: "/settings/integrations",
    icon: PuzzleIcon,
    adminOnly: true,
  },
  {
    name: "Team",
    path: "/settings/team",
    icon: UsersIcon,
    adminOnly: true,
  },
];

const settingsNavItemsByGroup: Record<SettingsNavGroupId, SettingsNavItem[]> = {
  account: settingsNavItems.filter((item) =>
    ["/settings/brand", "/settings/identity"].includes(item.path),
  ),
  setup: settingsNavItems.filter((item) =>
    ["/settings/installation", "/settings/integrations"].includes(item.path),
  ),
  administration: settingsNavItems.filter((item) =>
    ["/settings/team"].includes(item.path),
  ),
};

export function getSettingsNavGroups(isAdmin: boolean) {
  return SETTINGS_NAV_GROUP_ORDER.map((groupId) => ({
    id: groupId,
    label: SETTINGS_NAV_GROUP_LABELS[groupId],
    items: settingsNavItemsByGroup[groupId].filter(
      (item) => !item.adminOnly || isAdmin,
    ),
  })).filter((group) => group.items.length > 0);
}

export function isSettingsPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return normalized === "/settings" || normalized.startsWith("/settings/");
}

export function isChatPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return normalized === "/chat" || normalized.startsWith("/chat/");
}

const CHAT_CONVERSATION_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Conversation id from `/chat/[conversationId]`, if present and valid. */
export function parseChatConversationId(pathname: string): string | null {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  const segments = normalized.split("/").filter(Boolean);
  if (segments.length !== 2 || segments[0] !== "chat") {
    return null;
  }
  const id = segments[1];
  return CHAT_CONVERSATION_ID_PATTERN.test(id) ? id : null;
}

export function findSettingsRouteByPathname(pathname: string): SettingsNavItem | null {
  const exact = settingsNavItems.find((item) =>
    pathMatchesRoute(item.path, pathname),
  );
  if (exact) return exact;

  const normalized = pathname.replace(/\/+$/, "") || "/";
  return (
    settingsNavItems.find((item) => {
      const href = item.path.replace(/\/+$/, "") || "/";
      return normalized.startsWith(`${href}/`);
    }) ?? null
  );
}

export const appRoutes: Route[] = [
  {
    name: "Home",
    icon: HomeIcon,
    path: "/",
    hidden: false,
    group: "overview",
  },
  {
    name: "Donors",
    icon: UsersRoundIcon,
    path: "/donors",
    hidden: false,
    group: "fundraising",
    children: [
      {
        name: "Donor",
        icon: UserRoundIcon,
        path: "/donors/[donorId]",
        hidden: true,
      },
    ],
  },
  {
    name: "Donations",
    icon: GiftIcon,
    path: "/donations",
    hidden: false,
    group: "fundraising",
  },
  {
    name: "Regular Plans",
    icon: RefreshCcwIcon,
    path: "/recurring",
    hidden: false,
    group: "fundraising",
  },
  {
    name: "Campaigns",
    icon: ForwardIcon,
    path: "/campaigns",
    hidden: false,
    group: "fundraising",
    children: [
      {
        name: "Campaign",
        icon: ForwardIcon,
        path: "/campaigns/[campaignId]",
        hidden: true,
      },
    ],
  },
  {
    name: "Love Wall",
    icon: HeartIcon,
    path: "/love-wall",
    hidden: false,
    group: "fundraising",
  },
  {
    name: "Direct Emails",
    icon: MailIcon,
    path: "/direct-emails",
    hidden: false,
    group: "engagement",
  },
  {
    name: "Email Campaigns",
    icon: MailsIcon,
    path: "/email-campaigns",
    hidden: false,
    group: "engagement",
    disabled: true,
    disabledTitle: "Upcoming",
  },
  {
    name: "Media",
    icon: ImageIcon,
    path: "/media",
    hidden: false,
    group: "content",
  },
  {
    name: "Chat",
    icon: MessageSquareIcon,
    path: "/chat",
    hidden: false,
    group: "assistant",
  },
];

export function getSidebarNavGroups(isAdmin: boolean) {
  const topLevel = appRoutes.filter(
    (route) => !route.hidden && (!route.adminOnly || isAdmin),
  );

  return NAV_GROUP_ORDER.map((groupId) => ({
    id: groupId,
    label: NAV_GROUP_LABELS[groupId],
    routes: topLevel.filter((route) => route.group === groupId),
  })).filter((group) => group.routes.length > 0);
}

/** Match a route pattern (e.g. `/donors/[donorId]`) against a real pathname. */
export function pathMatchesRoute(routePath: string, pathname: string): boolean {
  const normalize = (p: string) => {
    const t = p.replace(/\/+$/, "");
    return t === "" ? "/" : t;
  };
  const r = normalize(routePath);
  const p = normalize(pathname);
  if (r === p) return true;
  const rSeg = r.split("/").filter(Boolean);
  const pSeg = p.split("/").filter(Boolean);
  if (rSeg.length !== pSeg.length) return false;
  return rSeg.every((seg, i) => seg.startsWith("[") || seg === pSeg[i]);
}

/** Ancestor chain from root nav route to the best match for `pathname`. */
export function findRouteChain(pathname: string): Route[] {
  const chain: Route[] = [];

  function visit(routes: Route[], ancestors: Route[]): boolean {
    for (const route of routes) {
      const path = [...ancestors, route];
      if (pathMatchesRoute(route.path, pathname)) {
        chain.push(...path);
        return true;
      }
      if (route.children?.length && visit(route.children, path)) {
        return true;
      }
    }
    return false;
  }

  visit(appRoutes, []);
  return chain;
}

export function findRouteByPathname(pathname: string): Route | null {
  const settingsRoute = findSettingsRouteByPathname(pathname);
  if (settingsRoute) {
    return {
      name: settingsRoute.name,
      icon: settingsRoute.icon,
      path: settingsRoute.path,
      hidden: true,
      ...(settingsRoute.adminOnly ? { adminOnly: true } : {}),
    };
  }

  function traverseRoutes(route: Route): Route | null {
    if (pathMatchesRoute(route.path, pathname)) {
      return route;
    }

    if (route.children) {
      for (const child of route.children) {
        const foundRoute = traverseRoutes(child);
        if (foundRoute) {
          return foundRoute;
        }
      }
    }

    return null;
  }

  for (const route of appRoutes) {
    const foundRoute = traverseRoutes(route);
    if (foundRoute) {
      return foundRoute;
    }
  }

  return null;
}

/** @deprecated Prefer findRouteByPathname with usePathname() from next/navigation */
export function findRouteByPath(path: string): Route | null {
  return findRouteByPathname(path);
}
